import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Plus,
  AlertCircle,
  DollarSign,
  Clock,
  Users,
  MapPin,
  Edit,
  Eye,
  Trash2
} from "lucide-react";
import { useBookingOptions } from "@/hooks/useBookings";
import {
  BookingOptionResponse,
  getBookingOptionStatusColor
} from "@/types/activities";
import CreateBookingOptionModal from "./CreateBookingOptionModal";
import UpdateBookingOptionModal from "./UpdateBookingOptionModal";
import BookingOptionDetailDrawer from "./BookingOptionDetailDrawer";

interface BookingOptionsListProps {
  bookingId: string;
  bookingStatus: string;
  canAddOptions?: boolean; // Deprecated - logic now handled internally
}

export default function BookingOptionsList({
  bookingId,
  bookingStatus,
  canAddOptions = true
}: BookingOptionsListProps) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [selectedOption, setSelectedOption] = useState<BookingOptionResponse | null>(null);

  const { data: options = [], loading, error, refetch } = useBookingOptions(bookingId);

  // Can only add new option if all existing options are rejected/unavailable
  const canCreateNewOption = options.length === 0 || options.every(
    opt => opt.status === 'Rejected' || opt.status === 'Unavailable'
  );

  // Get the active option (not rejected or unavailable)
  const activeOption = options.find(
    opt => opt.status !== 'Rejected' && opt.status !== 'Unavailable'
  );

  const formatPrice = (price?: number, currency: string = 'AED') => {
    if (!price) return "Pending";
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency
    }).format(price);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "TBD";
    return new Date(dateString).toLocaleDateString();
  };

  const handleEditOption = (option: BookingOptionResponse) => {
    setSelectedOption(option);
    setShowUpdateModal(true);
  };

  const handleViewOption = (option: BookingOptionResponse) => {
    setSelectedOption(option);
    setShowDetailDrawer(true);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Available':
        return '✅';
      case 'Unavailable':
        return '❌';
      case 'Exploring':
        return '🔍';
      case 'Approved':
        return '👍';
      case 'Rejected':
        return '👎';
      default:
        return '⏳';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Booking Options</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Booking Options</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load booking options: {error}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">
              Booking Options ({options.length})
            </CardTitle>
            {canCreateNewOption && (
              <Button
                size="sm"
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Alternative Option
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Cannot add new option warning */}
          {!canCreateNewOption && activeOption && (
            <Alert className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                <strong>Note:</strong> You can only add a new booking option after the current option (#{activeOption.optionOrder}) is either rejected by the organization or marked as unavailable.
              </AlertDescription>
            </Alert>
          )}

          {options.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Plus className="w-6 h-6 text-muted-foreground" />
              </div>
              <h3 className="text-sm font-medium text-foreground mb-2">No options found</h3>
              <p className="text-sm text-muted-foreground mb-4">
                There should be at least one option (auto-created by the backend). If you see this, there may be a data issue.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {options.map((option, index) => (
                <div
                  key={option.id}
                  className="border rounded-lg p-4 hover:bg-accent/5 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getStatusIcon(option.status)}</span>
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="font-medium text-sm">
                            Option #{option.optionOrder}: {option.serviceOptionName || 'Service Option'}
                          </h4>
                          {option.optionOrder === 1 && (
                            <Badge variant="outline" className="text-xs bg-blue-100 text-blue-800 border-blue-300">
                              🤖 Auto-Created
                            </Badge>
                          )}
                          {(option.status === 'Available' || option.status === 'Approved') && (
                            <Badge className="text-xs bg-purple-500 hover:bg-purple-600">
                              👁️ Visible to Org
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {option.vendorName || 'Vendor'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={`${getBookingOptionStatusColor(option.status)} text-xs`}
                      >
                        {option.status}
                      </Badge>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => handleViewOption(option)}
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => handleEditOption(option)}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {option.status === 'Available' && (
                    <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        <span>{formatPrice(option.proposedPrice)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{formatDate(option.proposedStartDate)}</span>
                      </div>
                      {option.proposedLocation && (
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          <span className="truncate">{option.proposedLocation}</span>
                        </div>
                      )}
                      {option.proposedCapacity && (
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          <span>{option.proposedCapacity} people</span>
                        </div>
                      )}
                    </div>
                  )}

                  {option.status === 'Unavailable' && option.unavailableReason && (
                    <div className="text-xs text-red-600 bg-red-50 p-2 rounded mt-2">
                      <strong>Unavailable:</strong> {option.unavailableReason}
                    </div>
                  )}

                  {option.status === 'Rejected' && option.counterStartDate && (
                    <div className="text-xs text-orange-600 bg-orange-50 p-2 rounded mt-2">
                      <strong>🔄 Countered:</strong> Dates: {formatDate(option.counterStartDate)} - {formatDate(option.counterEndDate)}
                      {option.counterCapacity && `, Capacity: ${option.counterCapacity}`}
                    </div>
                  )}

                  {option.status === 'Rejected' && option.rejectionReason && !option.counterStartDate && (
                    <div className="text-xs text-red-600 bg-red-50 p-2 rounded mt-2">
                      <strong>❌ Rejected:</strong> {option.rejectionReason.substring(0, 100)}{option.rejectionReason.length > 100 ? '...' : ''}
                    </div>
                  )}

                  {option.specialTerms && (
                    <div className="text-xs text-muted-foreground mt-2 p-2 bg-muted rounded">
                      <strong>Terms:</strong> {option.specialTerms}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Option Modal */}
      <CreateBookingOptionModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        bookingId={bookingId}
        onSuccess={() => {
          refetch();
          setShowCreateModal(false);
        }}
      />

      {/* Update Option Modal */}
      {selectedOption && (
        <UpdateBookingOptionModal
          open={showUpdateModal}
          onClose={() => {
            setShowUpdateModal(false);
            setSelectedOption(null);
          }}
          bookingId={bookingId}
          option={selectedOption}
          onSuccess={() => {
            refetch();
            setShowUpdateModal(false);
            setSelectedOption(null);
          }}
        />
      )}

      {/* Option Detail Drawer */}
      {selectedOption && (
        <BookingOptionDetailDrawer
          open={showDetailDrawer}
          onClose={() => {
            setShowDetailDrawer(false);
            setSelectedOption(null);
          }}
          option={selectedOption}
        />
      )}
    </>
  );
}